import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Coins, KeyRound, ShieldCheck, Sparkles, HelpCircle, Trophy, Rocket } from "lucide-react";
import Logo from "../components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import api, { fileUrl } from "../lib/api";

const FAQS = [
    {
        q: "Who can join Zapperus?",
        a: "Zapperus is strictly for students of registered schools. When you sign up, you'll pick your school from the list and register with your official school email address. Only students whose email domain matches the school's domain can join.",
    },
    {
        q: "How does the free trial work?",
        a: "Every new student gets a full 30-day free trial the moment they register. During the trial you can borrow any book on the storefront with no additional cost.",
    },
    {
        q: "What happens after the free trial ends?",
        a: "After 30 days, borrowing books requires an annual membership of ₹299 + GST (total ₹352.82) per year. You will still be able to post books, earn Zapp coins, and return any book you already borrowed before the trial ended.",
    },
    {
        q: "Can book owners keep using Zapperus for free?",
        a: "Yes. Posting books is forever free. Owners can keep listing books and earning 100 Zapp coins per posted book with no membership required.",
    },
    {
        q: "What are Zapp coins?",
        a: "Zapp coins are the in-app currency used for borrowing. You get 50 coins on sign-up, +100 for every book you post, and you spend coins when you borrow a book (price per day × days borrowed). Owners are credited the same coins the borrower spends.",
    },
    {
        q: "Can my Zapp coin balance go negative?",
        a: "Yes. If a borrow costs more than your current balance, your balance will show a negative number. You cannot borrow more until your balance is positive, but you can always earn back to positive by posting more books.",
    },
    {
        q: "Will Zapp coins expire?",
        a: "No, they never expire.",
    },
    {
        q: "How do I borrow a book?",
        a: "Browse the storefront, click 'Request Reservation' on the book you want, and wait for the owner to approve. Once approved, you'll receive a 4-digit start OTP — share it with the owner in person when they hand over the book.",
    },
    {
        q: "What is the return OTP?",
        a: "When the owner physically starts the borrowing (by entering your start OTP), a 4-digit return OTP is generated for the owner. They will share this return OTP with you only when you physically hand the book back. Entering it in the app settles the coins.",
    },
    {
        q: "How is the borrowing cost calculated?",
        a: "The cost equals the book's price per day multiplied by the number of days borrowed (minimum 1 day). Coins are debited from you and credited to the owner only when the return is completed.",
    },
    {
        q: "Can I see books from other schools?",
        a: "No. The storefront only shows books posted by students from your own school. This keeps the exchange safe, local and easy to hand books over in person.",
    },
    {
        q: "Do I need to pay to return a book I already borrowed?",
        a: "No. Returning any book you borrowed during your trial or membership is always free and available, even if your trial has ended in the meantime.",
    },
    {
        q: "How do I get my school onboarded?",
        a: "Ask your school's SPOC (Single Point Of Contact) to reach out to the Zapperus admin. Once the admin registers your school and email domain, students can start signing up immediately.",
    },
];

export default function Landing() {
    const [schools, setSchools] = useState([]);
    const [section, setSection] = useState("book_sharing"); // book_sharing | competitions

    useEffect(() => {
        api.get("/schools").then(({ data }) => setSchools(data)).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-paper">
            {/* Header */}
            <header className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
                <Link to="/" className="flex items-center" data-testid="landing-logo">
                    <Logo height={216} />
                </Link>
                <div className="flex items-center gap-2">
                    <a href="#faq" className="btn-ghost" data-testid="landing-faq-link">
                        <HelpCircle className="h-4 w-4" /> FAQ
                    </a>
                    <Link to="/login" className="btn-ghost" data-testid="landing-login-btn">Login</Link>
                    <Link to="/register" className="btn-primary" data-testid="landing-register-btn">Register</Link>
                </div>
            </header>

            {/* Section tabs */}
            <nav className="max-w-7xl mx-auto px-6" aria-label="Landing sections" data-testid="landing-tabs">
                <div className="inline-flex gap-1 rounded-full bg-white border border-parchment p-1 shadow-book">
                    <button
                        type="button"
                        onClick={() => setSection("book_sharing")}
                        data-testid="landing-tab-book-sharing"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                            section === "book_sharing" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                        }`}
                    >
                        <BookOpen className="h-4 w-4" /> Book Sharing
                    </button>
                    <button
                        type="button"
                        onClick={() => setSection("competitions")}
                        data-testid="landing-tab-competitions"
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                            section === "competitions" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"
                        }`}
                    >
                        <Trophy className="h-4 w-4" /> Competitions
                    </button>
                </div>
            </nav>

            {section === "competitions" ? (
                <section className="max-w-7xl mx-auto px-6 py-32 flex flex-col items-center text-center" data-testid="landing-competitions">
                    <div className="h-20 w-20 rounded-full bg-brick/10 text-brick flex items-center justify-center mb-6">
                        <Rocket className="h-9 w-9" />
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-library/10 text-library text-xs font-bold uppercase tracking-widest mb-4">
                        <Sparkles className="h-3.5 w-3.5" /> Coming soon
                    </div>
                    <h1 className="h-serif-hero text-4xl sm:text-5xl lg:text-6xl mb-4 max-w-3xl">
                        Reading <span className="text-brick">competitions</span> are on the way.
                    </h1>
                    <p className="text-lg text-ink/70 max-w-xl mb-8">
                        Compete with classmates on reading challenges, quiz rounds and book trivia — with Zapp coin prizes for winners.
                        We&apos;re getting the first season ready.
                    </p>
                    <button
                        type="button"
                        onClick={() => setSection("book_sharing")}
                        className="btn-secondary"
                        data-testid="landing-competitions-back"
                    >
                        Back to Book Sharing
                    </button>
                </section>
            ) : (
            <>
            {/* Hero */}
            <section className="max-w-7xl mx-auto px-6 pt-10 pb-24 grid lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-7 animate-fade-up">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-library/10 text-library text-xs font-bold uppercase tracking-widest mb-6">
                        <Sparkles className="h-3.5 w-3.5" /> Only for your school
                    </div>
                    <h1 className="h-serif-hero text-5xl sm:text-6xl lg:text-7xl mb-6">
                        Share books.<br />
                        Earn <span className="text-brick">Zapp Coins</span>.<br />
                        <span className="text-library">Read more.</span>
                    </h1>
                    <p className="text-lg text-ink/70 max-w-xl mb-8 leading-relaxed">
                        A trusted book exchange for classmates. Post a book you love, earn coins, borrow the next one you crave.
                        Everything happens inside your school — verified by your email domain.
                    </p>
                    <div className="flex flex-wrap gap-3 mb-6">
                        <Link to="/register" className="btn-primary" data-testid="landing-cta-register">
                            Get started — first month free
                        </Link>
                        <Link to="/login" className="btn-secondary" data-testid="landing-cta-login">
                            I already have an account
                        </Link>
                    </div>
                    <div className="mt-2 rounded-2xl border-2 border-brick/40 bg-brick/5 p-5 max-w-xl">
                        <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-full bg-brick text-white flex items-center justify-center flex-shrink-0 font-bold">₹</div>
                            <div>
                                <div className="font-serif text-xl sm:text-2xl font-bold text-ink leading-tight" data-testid="landing-pricing-headline">
                                    Forever free for Book Owners
                                </div>
                                <div className="text-base sm:text-lg font-semibold text-ink/80 mt-1" data-testid="landing-pricing-sub">
                                    After your free month, borrowing is <span className="text-brick font-bold">₹299 + GST / year</span>.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="lg:col-span-5 relative min-h-[420px]">
                    <img
                        src="https://customer-assets-v7afamib.emergentagent.net/job_zapp-book-exchange/artifacts/y6mtyy1m_Gemini_Generated_Image_kbhnyjkbhnyjkbhn.png"
                        alt="A student handing a book to a classmate in a school hallway"
                        className="w-full h-[420px] object-cover rounded-2xl border border-parchment shadow-book"
                        data-testid="landing-hero-image"
                    />
                    <div className="absolute -bottom-6 -right-4 bg-white border border-parchment rounded-2xl p-4 shadow-book-hover">
                        <div className="flex items-center gap-2">
                            <div className="h-10 w-10 rounded-full bg-zapp/20 flex items-center justify-center">
                                <Coins className="h-5 w-5 text-zapp-dark" />
                            </div>
                            <div>
                                <div className="font-serif font-semibold text-lg">+100</div>
                                <div className="text-xs uppercase tracking-widest text-ink/50">Per book posted</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="max-w-7xl mx-auto px-6 pb-24">
                <div className="max-w-2xl mb-10">
                    <div className="label-mini">How it works</div>
                    <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-ink">Three steps. Real books. Real coins.</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    <Step icon={<ShieldCheck className="h-5 w-5" />} n="01" title="Register with your school email" body="Choose your school; we verify your identity by the school's email domain." />
                    <Step icon={<BookOpen className="h-5 w-5" />} n="02" title="Post a book, earn +100 coins" body="Add title, author, cover image and daily price. Every listing earns you Zapp Coins." />
                    <Step icon={<KeyRound className="h-5 w-5" />} n="03" title="Reserve, exchange OTPs, return" body="Owner shares a start OTP to begin borrowing, a return OTP to close the loop." />
                </div>
            </section>

            {/* Trust: Schools onboarded — running logo carousel */}
            {schools.length > 0 && (
                <section className="pb-24 overflow-hidden" data-testid="landing-schools-section">
                    <div className="max-w-7xl mx-auto px-6 mb-10">
                        <div className="max-w-2xl">
                            <div className="label-mini">Trusted by schools</div>
                            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-ink">
                                Students from these schools are exchanging books.
                            </h2>
                        </div>
                    </div>

                    <div className="relative group" data-testid="landing-schools-carousel">
                        {/* Fade edges */}
                        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-paper to-transparent z-10" />
                        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-paper to-transparent z-10" />

                        <div className="flex w-max animate-marquee group-hover:[animation-play-state:paused]">
                            {[...schools, ...schools].map((s, idx) => (
                                <div
                                    key={`${s.id}-${idx}`}
                                    data-testid={idx < schools.length ? `landing-school-card-${s.id}` : undefined}
                                    className="mx-3 w-52 flex-shrink-0 bg-white border border-parchment rounded-2xl p-5 flex flex-col items-center text-center gap-3"
                                >
                                    {s.logo_file_id ? (
                                        <img
                                            src={fileUrl(s.logo_file_id)}
                                            alt={s.name}
                                            className="h-20 w-20 object-contain"
                                        />
                                    ) : (
                                        <div className="h-20 w-20 rounded-xl bg-paper border border-parchment flex items-center justify-center font-serif font-bold text-2xl text-ink/40">
                                            {s.name?.[0]?.toUpperCase() || "S"}
                                        </div>
                                    )}
                                    <div className="min-w-0 w-full">
                                        <div className="font-serif font-semibold text-ink text-sm leading-tight line-clamp-2">
                                            {s.name}
                                        </div>
                                        <div className="text-[11px] text-ink/50 mt-1 truncate">{s.branch}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FAQ */}
            <section id="faq" className="max-w-7xl mx-auto px-6 pb-24 scroll-mt-24">
                <div className="max-w-2xl mb-10">
                    <div className="label-mini">FAQ</div>
                    <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-ink">Everything you might want to know.</h2>
                    <p className="text-ink/60 mt-3">
                        Short answers to the questions students, owners and school SPOCs ask most often.
                    </p>
                </div>
                <Accordion type="single" collapsible className="max-w-4xl bg-white border border-parchment rounded-2xl divide-y divide-parchment overflow-hidden shadow-book" data-testid="landing-faq-list">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`item-${i}`} className="border-b-0 px-6" data-testid={`landing-faq-item-${i}`}>
                            <AccordionTrigger className="text-left text-base sm:text-lg font-serif font-semibold text-ink py-5 hover:no-underline">
                                {f.q}
                            </AccordionTrigger>
                            <AccordionContent className="text-ink/70 text-base leading-relaxed pb-5">
                                {f.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
                <div className="max-w-4xl text-sm text-ink/60 mt-6">
                    Still have questions? Reach out to your school's SPOC.
                </div>
            </section>
            </>
            )}

            <footer className="border-t border-parchment py-8 text-center text-sm text-ink/50">
                Built for students. Powered by trust.
            </footer>
        </div>
    );
}

function Step({ icon, n, title, body }) {
    return (
        <div className="bg-white border border-parchment rounded-2xl p-6 shadow-sm hover:shadow-book transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className="h-10 w-10 rounded-full bg-brick/10 text-brick flex items-center justify-center">
                    {icon}
                </div>
                <div className="font-serif text-3xl text-ink/20 font-semibold">{n}</div>
            </div>
            <div className="font-serif text-xl font-semibold mb-2">{title}</div>
            <div className="text-ink/70 text-sm leading-relaxed">{body}</div>
        </div>
    );
}
